import * as React from 'react';
import PropTypes from 'prop-types';
import { Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { formatWhen, formatDuration } from './constants';

const RUNS_SHOWN = 30;

const resultLabel = (run) => {
  if (!run) return '';
  if (run.skipped) return 'Skipped (no changes)';
  if (run.result === 'error') return 'Error';
  if (run.result === 'ok') return 'OK';
  if (!run.finishedAt) return 'Running';
  return run.result || '';
};

const countsSummary = (counts) => {
  if (!counts) return '';
  const parts = [];
  if (counts.studentsWritten != null) parts.push(`${counts.studentsWritten} updated`);
  if (counts.studentsCreated != null) parts.push(`${counts.studentsCreated} new`);
  if (counts.studentsUnchanged != null) parts.push(`${counts.studentsUnchanged} unchanged`);
  if (counts.studentsMissing != null) parts.push(`${counts.studentsMissing} missing`);
  return parts.join(', ');
};

class SyncPanel extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    config: PropTypes.object,
    liveState: PropTypes.object,
    busy: PropTypes.bool,
    onRun: PropTypes.func.isRequired,
    statusLine: PropTypes.node,
  };

  state = { expanded: null };

  toggle = (runId) => {
    this.setState((s) => ({ expanded: s.expanded === runId ? null : runId }));
  };

  render() {
    const { classes, config, liveState, busy, onRun, statusLine } = this.props;
    const { expanded } = this.state;
    const status = (liveState && liveState.status) || {};
    const runs = Object.entries((liveState && liveState.runs) || {})
      .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
      .slice(0, RUNS_SHOWN);
    const linked = !!(config && config.tenantId);
    const inFlight = !!status.inFlight;
    return (
      <Paper className={classes.section}>
        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
          Sync
        </Typography>
        <div className={classes.row}>
          <Button variant="contained" color="primary" disabled={!linked || inFlight || busy} onClick={onRun}>
            {inFlight ? 'Sync in progress...' : 'Sync now'}
          </Button>
          <Typography className={classes.muted} style={{ marginLeft: 16 }}>
            {status.lastRunAt
              ? `Last run ${formatWhen(status.lastRunAt)}: ${status.lastResult || ''}${
                  status.lastError ? ` (${status.lastError})` : ''
                }`
              : 'No runs yet.'}
            {status.lastSuccessAt ? ` Last successful sync ${formatWhen(status.lastSuccessAt)}.` : ''}
          </Typography>
        </div>
        {status.counts && (
          <Typography className={classes.muted} style={{ marginTop: 8 }}>
            {countsSummary(status.counts)}
            {status.pendingCount ? ` · ${status.pendingCount} need review` : ''}
          </Typography>
        )}
        {statusLine}
        {runs.length > 0 && (
          <div className={classes.tableScroll}>
            <Table padding="dense" style={{ marginTop: 12 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Students</TableCell>
                  <TableCell>Classes</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map(([runId, run]) => {
                  const warnings = Array.isArray(run.warnings) ? run.warnings : [];
                  const c = run.counts || {};
                  const open = expanded === runId;
                  return (
                    <React.Fragment key={runId}>
                      <TableRow
                        hover
                        onClick={() => warnings.length && this.toggle(runId)}
                        style={{ cursor: warnings.length ? 'pointer' : 'default' }}
                      >
                        <TableCell className={classes.cellNarrow}>{formatWhen(run.startedAt)}</TableCell>
                        <TableCell>{run.trigger || ''}</TableCell>
                        <TableCell>
                          {resultLabel(run)}
                          {run.error && run.error.message ? (
                            <span className={classes.error}> {run.error.message}</span>
                          ) : null}
                        </TableCell>
                        <TableCell>{run.skipped ? '' : countsSummary(c)}</TableCell>
                        <TableCell>
                          {run.skipped || c.classes == null
                            ? ''
                            : `${c.classesMatched != null ? c.classesMatched : '?'}/${c.classes} matched`}
                        </TableCell>
                        <TableCell>{formatDuration(run.durationMs)}</TableCell>
                        <TableCell>{warnings.length ? `${warnings.length}${open ? ' ▾' : ' ▸'}` : ''}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            {warnings.map((w) => (
                              <Typography key={w.code} className={classes.muted} style={{ fontSize: 13 }}>
                                <strong>{w.code}</strong>
                                {w.count != null ? ` ×${w.count}` : ''}: {w.message}
                                {w.sample && w.sample.length ? (
                                  <span className={classes.sample}> e.g. {w.sample.join(', ')}</span>
                                ) : null}
                              </Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Paper>
    );
  }
}

export default SyncPanel;
